import { defaultSettings } from '../state/settingsState';

export const finalHoleDiameterOptions = [
  { value: 1, label: '3-3/4"' },
  { value: 2, label: '4"' },
  { value: 3, label: '4-3/4"' },
  { value: 4, label: '5-7/8"' },
  { value: 5, label: '6"' },
  { value: 6, label: '6-1/2"' },
  { value: 7, label: '7-7/8"' },
  { value: 8, label: '8-1/2"' },
  { value: 9, label: '8-3/4"' },
  { value: 10, label: '9-1/2"' },
  { value: 11, label: '9-7/8"' },
  { value: 12, label: '10-5/8"' },
  { value: 13, label: '12-1/4"' },
  { value: 14, label: '14-3/4"' },
  { value: 15, label: '16"' },
  { value: 16, label: '17-1/2"' }
];

const getParameterValue = (input, obj) => {
  if (typeof input !== 'string') return undefined;
  const norm = input.trim().toLowerCase();
  if (norm in obj) {
    return obj[norm].val;
  }
  return undefined;
};

const locationSensitivity = (location, residential, sensitive, settings) => {
  const { locationValues, weights } = settings;
  const locationValue = getParameterValue(location, locationValues) || 0;
  let residentialValue = residential ? 1 : 0;
  const sensitiveValue = sensitive ? 1 : 0;

  if (location === 'medium') {
    residentialValue = 1;
  }

  const locationScore = locationValue * weights.locationWeight;
  const residentialScore = residentialValue * weights.residentialWeight;
  const sensitiveScore = sensitiveValue * weights.sensitiveWeight;

  let totalScore = locationScore + residentialScore + sensitiveScore;
  return Math.min(totalScore, 1);
};

const rigCapacity = (rigType, location, settings) => {
  if (!rigType) return 0;
  const { rigCapacityValues, weights, thresholds } = settings;
  const norm = rigType.trim().toLowerCase();
  const rigValue = getParameterValue(norm, rigCapacityValues) || 0;

  const locValue = thresholds.locationMultipliers[(location || '').trim().toLowerCase()] || 1;
  let totalScore = (rigValue * locValue) / 15;
  totalScore = Math.min(totalScore, 1);
  return totalScore * weights.rigCapacityWeight;
};

const siteLevelScore = (location, residential, sensitive, rigType, settings) => {
  const { roundingDecimals, categoryWeights } = settings;
  const locationScore = locationSensitivity(location, residential, sensitive, settings);
  const rigScore = rigCapacity(rigType, location, settings);
  const siteScore = locationScore * categoryWeights.location + rigScore * categoryWeights.rigCapacity;

  return {
    siteScore: Math.round(siteScore * roundingDecimals) / roundingDecimals,
    locationScore: Math.round(locationScore * roundingDecimals) / roundingDecimals,
    rigScore: Math.round(rigScore * roundingDecimals) / roundingDecimals
  };
};

const firstHoleDiameter = (finalHoleDiameter, numberOfSections, settings) => {
  if (!finalHoleDiameter || !numberOfSections) return 0;
  const { wellboreGeometry, weights, thresholds } = settings;
  const firstHoleDiam = wellboreGeometry[finalHoleDiameter][numberOfSections - 1];
  let firstHoleScore;

  const { low, high } = thresholds.firstHoleDiameter;
  if (firstHoleDiam < low) {
    firstHoleScore = 0;
  } else if (firstHoleDiam >= low && firstHoleDiam < high) {
    firstHoleScore = 0.5;
  } else {
    firstHoleScore = 1;
  }

  return firstHoleScore * weights.firstHoleDiameterWeight;
};

const firstBOP = (finalHoleDiameter, numberOfSections, settings) => {
  if (!finalHoleDiameter || !numberOfSections) return 0;
  if (numberOfSections === 1) return 0;

  const { wellboreGeometry, weights, thresholds } = settings;
  const firstBOPDiameter = wellboreGeometry[finalHoleDiameter][numberOfSections - 2];
  let firstBOPScore;

  const { t1, t2, t3 } = thresholds.firstBOP;
  if (firstBOPDiameter < t1) {
    firstBOPScore = 0;
  } else if (firstBOPDiameter >= t1 && firstBOPDiameter < t2) {
    firstBOPScore = 0.33;
  } else if (firstBOPDiameter >= t2 && firstBOPDiameter < t3) {
    firstBOPScore = 0.66;
  } else {
    firstBOPScore = 1;
  }

  return firstBOPScore * weights.firstBOPDiameterWeight;
};

const nrOfSections = (numberOfSections, settings) => {
  if (!numberOfSections) return 0;
  const { weights } = settings;
  const numberOfSectionScore = weights.numberOfSectionsScore[numberOfSections - 1];
  return numberOfSectionScore * weights.numberOfSectionsWeight;
};

const vDepth = (verticalDepth, settings) => {
  const { weights, thresholds } = settings;
  const verticalDepthScore = Math.ceil(Math.min(1, verticalDepth / thresholds.maxDepth) * 100) / 100;
  return verticalDepthScore * weights.verticalDepthWeight;
};

const maxTemp = (maxTemperature, settings) => {
  const { weights, thresholds } = settings;
  const { min, max } = thresholds.temperature;
  let maxTemperatureScore;

  if (maxTemperature < min) {
    maxTemperatureScore = 0;
  } else if (maxTemperature >= min && maxTemperature < max) {
    maxTemperatureScore = (maxTemperature - min) / (max - min);
  } else {
    maxTemperatureScore = 1;
  }

  return maxTemperatureScore * weights.maxTemperatureWeight;
};

const maxPress = (maxPressure, settings) => {
  const { weights, thresholds } = settings;
  const pressThresholds = thresholds.pressure;

  const getPressureScore = (p) => {
    if (p <= pressThresholds[0]) return 0;
    if (p <= pressThresholds[1]) return 0.2;
    if (p <= pressThresholds[2]) return 0.4;
    if (p <= pressThresholds[3]) return 0.6;
    if (p <= pressThresholds[4]) return 0.8;
    return 1;
  };

  const maxPressureScore = getPressureScore(maxPressure * thresholds.barToPsi);
  return maxPressureScore * weights.maxPressureWeight;
};

const stim = (stimulation, settings) => (stimulation ? 1 : 0) * settings.weights.stimulationWeight;
const scal = (scaling, settings) => (scaling ? 1 : 0) * settings.weights.scalingWeight;
const corr = (corrosion, settings) => (corrosion ? 1 : 0) * settings.weights.corrosionWeight;

const designScore = (reEntry, finalHoleDiameter, numberOfSections, verticalDepth, maxTemperature, maxPressure, stimulation, scaling, corrosion, settings) => {
  if (reEntry) return 1;

  const score = firstHoleDiameter(finalHoleDiameter, numberOfSections, settings) +
    firstBOP(finalHoleDiameter, numberOfSections, settings) +
    nrOfSections(numberOfSections, settings) +
    vDepth(verticalDepth, settings) +
    maxTemp(maxTemperature, settings) +
    maxPress(maxPressure, settings) +
    stim(stimulation, settings) +
    scal(scaling, settings) +
    corr(corrosion, settings);

  return Math.round(score * settings.roundingDecimals) / settings.roundingDecimals;
};

const circTemp = (circulatingTemp, settings) => {
  const circTempScore = getParameterValue(circulatingTemp, settings.circulationValues) || 0;
  return circTempScore * settings.weights.circulatingTempWeight;
};

const pressRegime = (pressureRegime, settings) => {
  const pressRegimeScore = getParameterValue(pressureRegime, settings.pressRegimeValues) || 0;
  return pressRegimeScore * settings.weights.pressureRegimeWeight;
};

const uboAndMpd = (ubompd, settings) => (ubompd ? 1 : 0) * settings.weights.ubompdWeight;

const constructionScore = (circulatingTemp, pressureRegime, ubompd, settings) => {
  const score = circTemp(circulatingTemp, settings) + pressRegime(pressureRegime, settings) + uboAndMpd(ubompd, settings);
  return Math.round(score * settings.roundingDecimals) / settings.roundingDecimals;
};

const ucs = (unconfinedCompressionStrength, settings) => {
  const ucsScore = getParameterValue(unconfinedCompressionStrength, settings.ucsValues) || 0;
  return ucsScore * settings.weights.ucsWeight;
};

const intrbdd = (interbedded, settings) => (interbedded ? 1 : 0) * settings.weights.interbeddedWeight;

const ddi = (drillingDifficultyIndex, settings) => {
  const ddiScore = getParameterValue(drillingDifficultyIndex, settings.ddiValues);
  return (ddiScore || 0) * settings.weights.ddiWeight;
};

const mltLateral = (multilateral, settings) => (multilateral ? 1 : 0) * settings.weights.multiLateralWeight;
const intrception = (interception, settings) => (interception ? 1 : 0) * settings.weights.interceptionWeight;

const drillingComplexityScore = (unconfinedCompressionStrength, interbedded, drillingDifficultyIndex, multilateral, interception, settings) => {
  const score = ucs(unconfinedCompressionStrength, settings) +
    intrbdd(interbedded, settings) +
    ddi(drillingDifficultyIndex, settings) +
    mltLateral(multilateral, settings) +
    intrception(interception, settings);

  return Math.round(score * settings.roundingDecimals) / settings.roundingDecimals;
};

const phse = (phase, settings) => {
  const phaseScore = getParameterValue(phase, settings.phaseValues) || 0;
  return phaseScore * settings.weights.phaseWeight;
};

const hdroCrbons = (hydroCarbons, settings) => (hydroCarbons ? 1 : 0) * settings.weights.hydrocarbonWeight;
const txcGas = (toxicGas, settings) => (toxicGas ? 1 : 0) * settings.weights.toxicGasWeight;

const wellControlScore = (phase, hydroCarbons, toxicGas, settings) => {
  const score = phse(phase, settings) + hdroCrbons(hydroCarbons, settings) + txcGas(toxicGas, settings);
  return Math.round(score * 100) / 100;
};

const wellLevelScore = (
  reEntry, finalHoleDiameter, numberOfSections, verticalDepth, maxTemperature, maxPressure,
  stimulation, scaling, corrosion, circulatingTemp, pressureRegime, ubompd,
  unconfinedCompressionStrength, interbedded, drillingDifficultyIndex, multilateral, interception,
  phase, hydroCarbons, toxicGas, settings
) => {
  const { roundingDecimals, categoryWeights } = settings;
  const dsgnScore = designScore(reEntry, finalHoleDiameter, numberOfSections, verticalDepth, maxTemperature, maxPressure, stimulation, scaling, corrosion, settings);
  const cnstrctnScore = constructionScore(circulatingTemp, pressureRegime, ubompd, settings);
  const drlgCmplxScore = drillingComplexityScore(unconfinedCompressionStrength, interbedded, drillingDifficultyIndex, multilateral, interception, settings);
  const wllCtrlScore = wellControlScore(phase, hydroCarbons, toxicGas, settings);

  const wellScore = dsgnScore * categoryWeights.design +
    cnstrctnScore * categoryWeights.construction +
    drlgCmplxScore * categoryWeights.drillingComplexity +
    wllCtrlScore * categoryWeights.wellControl;

  return {
    wellScore: Math.round(wellScore * roundingDecimals) / roundingDecimals,
    dsgnScore,
    cnstrctnScore,
    drlgCmplxScore,
    wllCtrlScore
  };
};

// Convert imperial inputs to metric so scoring functions always work in metric
const toMetric = (obj) => {
  if (obj.units !== 'imperial') return obj;
  return {
    ...obj,
    verticalDepth: obj.verticalDepth / 3.28084,
    maxTemperature: (obj.maxTemperature - 32) * 5 / 9,
    maxPressure: obj.maxPressure / 14.5038
  };
};

export const getComplexityIndex = (obj, settings = defaultSettings) => {
  const data = toMetric(obj);
  const siteLvlScore = siteLevelScore(data.location, data.residential, data.sensitive, data.rigCapacity, settings);

  const wellLvlScore = wellLevelScore(
    data.reEntry,
    data.finalHoleDiameter,
    data.numberOfSections,
    data.verticalDepth,
    data.maxTemperature,
    data.maxPressure,
    data.stimulation,
    data.scaling,
    data.corrosion,
    data.circulatingTemp,
    data.pressureRegime,
    data.ubompd,
    data.ucs,
    data.interbedded,
    data.ddi,
    data.multilateral,
    data.interception,
    data.phase,
    data.hydroCarbons,
    data.toxicGas,
    settings
  );

  return { ...siteLvlScore, ...wellLvlScore };
};

export const getSettingsValues = (settings = defaultSettings) => ({
  locationValues: settings.locationValues,
  rigCapacityValues: settings.rigCapacityValues,
  circulationValues: settings.circulationValues,
  pressRegimeValues: settings.pressRegimeValues,
  ucsValues: settings.ucsValues,
  ddiValues: settings.ddiValues,
  phaseValues: settings.phaseValues,
  weights: settings.weights,
  categoryWeights: settings.categoryWeights,
  wellboreGeometry: settings.wellboreGeometry
});

export const locationValues = defaultSettings.locationValues;
export const rigCapacityValues = defaultSettings.rigCapacityValues;
export const circulationValues = defaultSettings.circulationValues;
export const pressRegimeValues = defaultSettings.pressRegimeValues;
export const ucsValues = defaultSettings.ucsValues;
export const ddiValues = defaultSettings.ddiValues;
export const phaseValues = defaultSettings.phaseValues;
export const weights = defaultSettings.weights;
export const categoryWeights = defaultSettings.categoryWeights;
export const wellboreGeometry = defaultSettings.wellboreGeometry;
